/**
 * Rspack/webpack chunk-load helpers.
 * Pure functions — no React or Bugsnag. Error shape is duck-typed from runtime messages:
 *   Loading chunk N failed.
 *   (timeout: url) | (error: url) | (missing: url)
 */

export const CHUNK_RELOAD_STORAGE_KEY = 'fm_chunk_reload';

const DEFAULT_RETRIES = 2;
const DEFAULT_RETRY_DELAY = 500;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getName = (error) => error?.name || error?.originalError?.name || '';

/**
 * @returns {'timeout' | 'error' | 'missing' | 'unknown' | null}
 * null when the value does not look like a chunk load failure.
 */
export const getChunkLoadCause = (error) => {
  const messages = [error?.message, error?.originalError?.message]
    .filter(Boolean)
    .map(String);

  for (const message of messages) {
    if (/\(timeout:/i.test(message)) return 'timeout';
    if (/\(missing:/i.test(message)) return 'missing';
    if (/\(error:/i.test(message)) return 'error';
  }

  const combined = messages.join('\n');
  if (
    getName(error) === 'ChunkLoadError' ||
    /Loading chunk .+ failed/i.test(combined)
  ) {
    return 'unknown';
  }
  return null;
};

export const isChunkLoadError = (error) => getChunkLoadCause(error) !== null;

export const isChunkLoadTimeout = (error) =>
  getChunkLoadCause(error) === 'timeout';

export const isStaleChunkLoadError = (error) => {
  const cause = getChunkLoadCause(error);
  return cause === 'error' || cause === 'missing';
};

/**
 * Retries a dynamic import with exponential backoff.
 * Rspack drops a failed chunk from its registry, so a new import() issues a fresh request.
 *
 * @param {() => Promise<unknown>} load
 * @param {{ retries?: number, retryDelay?: number, shouldRetry?: (error: unknown) => boolean }} [options]
 * Default shouldRetry is timeout-only (stale 404s fail fast).
 */
export const loadWithRetry = (
  load,
  {
    retries = DEFAULT_RETRIES,
    retryDelay = DEFAULT_RETRY_DELAY,
    shouldRetry = isChunkLoadTimeout,
  } = {},
) => {
  const attempt = (remaining, wait) =>
    load().catch((error) => {
      if (remaining <= 0 || !shouldRetry(error)) throw error;
      return delay(wait).then(() => attempt(remaining - 1, wait * 2));
    });

  return attempt(retries, retryDelay);
};

const getDefaultStorage = () => {
  try {
    if (typeof sessionStorage !== 'undefined') return sessionStorage;
  } catch {
    // Private mode / blocked storage
  }
  return null;
};

/**
 * One-shot page reload for post-deploy stale chunk hashes.
 * @returns {boolean} true when a reload was triggered
 */
export const tryReloadForStaleChunk = (
  error,
  {
    storageKey = CHUNK_RELOAD_STORAGE_KEY,
    storage = getDefaultStorage(),
    reload = () => {
      if (typeof window !== 'undefined') window.location.reload();
    },
  } = {},
) => {
  if (!isStaleChunkLoadError(error) || !storage) return false;
  if (storage.getItem(storageKey)) return false;
  storage.setItem(storageKey, '1');
  reload();
  return true;
};

export const clearChunkReloadFlag = (
  storageKey = CHUNK_RELOAD_STORAGE_KEY,
  storage = getDefaultStorage(),
) => {
  storage?.removeItem(storageKey);
};
